import React, { createContext, useState, useContext, useEffect } from 'react';
import { walletAPI } from './services/api';
import { authStorage } from './services/storage';

const WalletContext = createContext();

export function WalletProvider({ children }) {
  const [wallets, setWallets] = useState([]);
  const [selectedWallets, setSelectedWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadWallets = async () => {
    // Don't try to load wallets if there's no token
    const token = authStorage.getToken();
    console.log('[WalletContext] loadWallets called, token exists:', !!token);
    
    if (!token) {
      console.log('[WalletContext] No token, skipping wallet load');
      setLoading(false);
      return;
    }

    try {
      console.log('[WalletContext] Fetching wallets from API...');
      const response = await walletAPI.list();
      console.log('[WalletContext] API response:', response.data);
      
      // Filter out "All Wallets" option (id=0)
      const realWallets = response.data.filter(w => w.id !== 0);
      console.log('[WalletContext] Real wallets:', realWallets.length);
      
      setWallets(realWallets);
      
      // Default to first real wallet when loaded (only if none selected and first load)
      if (realWallets.length > 0 && !hasLoaded) {
        console.log('[WalletContext] Auto-selecting first wallet:', realWallets[0].name);
        setSelectedWallets([realWallets[0].id]);
        setHasLoaded(true);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('[WalletContext] Error loading wallets:', error);
      setLoading(false);
    }
  };

  // Initial load attempt
  useEffect(() => {
    console.log('[WalletContext] Initial useEffect running');
    loadWallets();
  }, []);

  // Listen for auth ready event
  useEffect(() => {
    const handleAuthReady = () => {
      console.log('[WalletContext] authReady event received!');
      loadWallets();
    };
    
    window.addEventListener('authReady', handleAuthReady);
    console.log('[WalletContext] Listening for authReady event');
    
    return () => {
      window.removeEventListener('authReady', handleAuthReady);
    };
  }, [hasLoaded]);

  // Also try loading after a delay as fallback
  useEffect(() => {
    const timer = setTimeout(() => {
      if (wallets.length === 0 && authStorage.getToken()) {
        console.log('[WalletContext] Fallback: retrying wallet load after delay');
        loadWallets();
      }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [wallets.length]);

  const handleWalletToggle = (walletId) => {
    console.log('handleWalletToggle called with:', walletId);
    console.log('Current selectedWallets:', selectedWallets);
    
    const wallet = wallets.find(w => w.id === walletId);
    if (!wallet) return;

    if (selectedWallets.includes(walletId)) {
      const newSelection = selectedWallets.filter(id => id !== walletId);
      if (newSelection.length === 0) {
        alert('You must have at least one pot selected');
        return;
      }
      setSelectedWallets(newSelection);
      return;
    }

    if (selectedWallets.length > 0) {
      const selectedWalletsCurrency = wallets.find(w => w.id === selectedWallets[0])?.currency;
      if (selectedWalletsCurrency && wallet.currency !== selectedWalletsCurrency) {
        setSelectedWallets([walletId]);
        return;
      }
    }

    setSelectedWallets([...selectedWallets, walletId]);
  };

  const getSelectedCurrency = () => {
    const firstWallet = wallets.find(w => w.id === selectedWallets[0]);
    return firstWallet?.currency || 'EUR';
  };

  const getCurrencySymbol = (currency) => {
    const symbols = {
      'EUR': '€', 'USD': '$', 'GBP': '£', 'CHF': 'CHF',
      'JPY': '¥', 'CAD': 'C$', 'AUD': 'A$', 'CNY': '¥', 'INR': '₹'
    };
    return symbols[currency] || currency;
  };

  const getSelectedWalletsDisplay = () => {
    if (selectedWallets.length === 0) return 'No Pot Selected';
    if (selectedWallets.length === 1) {
      const wallet = wallets.find(w => w.id === selectedWallets[0]);
      return wallet?.name || 'Unknown';
    }
    return `${selectedWallets.length} Pots (${getSelectedCurrency()})`;
  };

  return (
    <WalletContext.Provider value={{
      wallets,
      selectedWallets,
      setSelectedWallets,
      handleWalletToggle,
      getSelectedCurrency,
      getCurrencySymbol,
      getSelectedWalletsDisplay,
      loading,
      refreshWallets: loadWallets
    }}>
      {children}
    </WalletContext.Provider>
  );
}

export function useWallets() {
  const context = useContext(WalletContext);
  if (!context) {
    throw new Error('useWallets must be used within WalletProvider');
  }
  return context;
}
